import { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType) {
  const [value, setValue] = useState<ValueType>(() => {
    try {
      const storedValue = localStorage.getItem(key);
      if (storedValue === null) return defaultValue;
      return JSON.parse(storedValue) as ValueType;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: ValueType | ((prev: ValueType) => ValueType)) => {
    setValue((currentValue) => {
      const result =
        typeof newValue === 'function'
          ? (newValue as (prev: ValueType) => ValueType)(currentValue as ValueType)
          : (newValue as ValueType);
      try {
        localStorage.setItem(key, JSON.stringify(result));
      } catch {
        // ignore quota or serialization errors
      }
      return result as ValueType;
    });
  };

  return [value, setValueInLocalStorage] as const;
}
